import { useAsync } from '@shared/async';
import React from 'react';

import { ConfirmationDialog } from '@/components/delete-confirmation-dialog';
import { BoardMeeting } from '@/types/board-meetings';

import { useBoardMeetingService } from '../api';

type UncancelBoardMeetingDialogProps = {
  /**
   * The board meeting to uncancel. If null, this modal will be closed.
   */
  boardMeeting: BoardMeeting | null;

  /**
   * Event handler when the user cancels the operation.
   */
  onCancel: () => void;

  /**
   * Event handler once the uncancel has occurred on the server.
   */
  onUncancelComplete: () => void;
};

/**
 * Handles uncancellation (restoration) of a Board Meeting.
 */
export const UncancelBoardMeetingDialog = ({ boardMeeting, onCancel, onUncancelComplete }: UncancelBoardMeetingDialogProps) => {
  const { uncancelBoardMeeting } = useBoardMeetingService();
  const uncancelAsync = useAsync(uncancelBoardMeeting);

  return (
    <ConfirmationDialog
      open={boardMeeting !== null}
      title="Restore Board Meeting"
      message={`Are you sure you want to restore this canceled board meeting scheduled for ${boardMeeting?.startTime?.toLocaleString()}?`}
      confirmLabel="Restore Meeting"
      cancelLabel="Keep Canceled"
      confirmColor="primary"
      itemName="Board Meeting"
      successTitle="Meeting Restored"
      successMessage="Board meeting has been successfully restored."
      onCancel={onCancel}
      onActionComplete={onUncancelComplete}
      request={uncancelAsync}
      parameters={boardMeeting ? [boardMeeting.id] : []}
    />
  );
};